import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  View, Text, SectionList, ActivityIndicator, RefreshControl,
  TextInput, TouchableOpacity, StyleSheet
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';

const Badge = ({ type }) => {
  const isIn = type === 'restock';
  const bg = isIn ? PADIPRO.primarySoft : PADIPRO.dangerSoft;
  const fg = isIn ? PADIPRO.primary : PADIPRO.danger;
  const label = isIn ? 'Barang Masuk' : 'Barang Keluar';
  const Icon = isIn ? Ionicons : MaterialCommunityIcons;
  const iconName = isIn ? 'arrow-down-circle' : 'arrow-up-circle';
  return (
    <View style={[s.badge, { backgroundColor: bg, borderColor: fg }]}>
      <Icon name={iconName} size={14} color={fg} style={{ marginRight: 6 }} />
      <Text style={{ color: fg, fontWeight: '700', fontSize: 12 }}>{label}</Text>
    </View>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  // 21 Agu 2025 14:33
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatDateOnly = (iso) => {
  const d = new Date(iso);
  // Kam, 21 Agu 2025
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
  });
};

export default function HistoryScreen({ route }) {
  const { token, embedded = false, seasonId = null, refreshKey = 0 } = route.params;
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | restock | use

  const fetchData = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_BASE}/transactions`, {
        params: { season_id: seasonId || 'active' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setRaw(r.data || []);
    } catch (err) {
      console.log('history load error:', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [seasonId, refreshKey])
  );

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API_BASE}/transactions`, {
        params: { season_id: seasonId || 'active' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setRaw(r.data || []);
    } catch (e) {
      // ignore
    } finally {
      setRefreshing(false);
    }
  };

  // filter + search
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (raw || []).filter((it) => {
      if (filter !== 'all' && it.action !== filter) return false;
      if (!q) return true;
      const hay = `${it.pesticide_name || ''} ${it.user_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [raw, filter, query]);

  // ringkasan
  const summary = useMemo(() => {
    let inCount = 0, inQty = 0, outCount = 0, outQty = 0;
    filtered.forEach((t) => {
      if (t.action === 'restock') { inCount++; inQty += Number(t.qty || 0); }
      else if (t.action === 'use') { outCount++; outQty += Number(t.qty || 0); }
    });
    return { inCount, inQty, outCount, outQty, total: filtered.length };
  }, [filtered]);

  // group by date (YYYY-MM-DD)
  const sections = useMemo(() => {
    const byDate = new Map();
    for (const t of filtered) {
      const d = new Date(t.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push(t);
    }
    // sort desc by date
    const keys = Array.from(byDate.keys()).sort((a,b)=>b.localeCompare(a));
    return keys.map(k => ({
      title: k,
      data: byDate.get(k).sort((a,b)=> new Date(b.created_at) - new Date(a.created_at))
    }));
  }, [filtered]);

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator />
        <Text style={{ marginTop: 8, color: PADIPRO.muted }}>Memuat riwayat…</Text>
      </View>
    );
  }

  return (
    <View style={s.screen}>
      {!embedded && (
        <EnterpriseHeader
          title="Riwayat Transaksi"
          subtitle="Pantau histori restock dan pemakaian secara real-time"
          right={(
            <TouchableOpacity onPress={onRefresh} style={s.headerActionBtn} activeOpacity={0.85} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="refresh" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          )}
        />
      )}

      <View style={[s.summaryCard, embedded && { marginTop: 0 }]}>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.summaryLabel}>Masuk (trx)</Text>
          <Text style={[s.summaryValue, { color: PADIPRO.primary }]}>{summary.inCount}</Text>
          <Text style={s.summarySub}>{summary.inQty} unit</Text>
        </View>
        <View style={s.divider} />
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.summaryLabel}>Keluar (trx)</Text>
          <Text style={[s.summaryValue, { color: PADIPRO.danger }]}>{summary.outCount}</Text>
          <Text style={s.summarySub}>{summary.outQty} unit</Text>
        </View>
        <View style={s.divider} />
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.summaryLabel}>Total</Text>
          <Text style={s.summaryValue}>{summary.total}</Text>
          <Text style={s.summarySub}>semua</Text>
        </View>
      </View>

      {/* Search & Filters */}
      <View style={s.searchWrap}>
        <Ionicons name="search" size={16} color={PADIPRO.mutedSoft} style={{ marginHorizontal: 8 }} />
        <TextInput
          placeholder="Cari nama pestisida / pengguna…"
          placeholderTextColor={PADIPRO.mutedSoft}
          value={query}
          onChangeText={setQuery}
          style={{ flex: 1, paddingVertical: 8, minHeight: 44, color: PADIPRO.text }}
        />
        {query !== '' && (
          <TouchableOpacity
            onPress={() => setQuery('')}
            style={{ paddingHorizontal: 8 }}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close-circle" size={16} color={PADIPRO.mutedSoft} />
          </TouchableOpacity>
        )}
      </View>

      <View style={s.filterRow}>
        <FilterChip label="Semua" active={filter === 'all'} onPress={() => setFilter('all')} />
        <FilterChip label="Masuk" active={filter === 'restock'} onPress={() => setFilter('restock')} />
        <FilterChip label="Keluar" active={filter === 'use'} onPress={() => setFilter('use')} />
      </View>

      {/* List */}
      {sections.length === 0 ? (
        <View style={[s.center, { marginTop: 20 }]}>
          <Ionicons name="time-outline" size={26} color={PADIPRO.mutedSoft} />
          <Text style={{ marginTop: 8, color: PADIPRO.muted }}>Belum ada data untuk filter saat ini.</Text>
        </View>
      ) : (
        <SectionList
          sections={sections}
          keyExtractor={(item) => String(item.transaction_id)}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
          renderSectionHeader={({ section }) => (
            <Text style={s.sectionTitle}>{formatDateOnly(section.data?.[0]?.created_at)}</Text>
          )}
          renderItem={({ item }) => (
            <View style={s.card}>
              <View style={s.cardHeader}>
                <Badge type={item.action} />
                <Text style={s.qtyText}>{item.qty} {item.unit}</Text>
              </View>

              <Text style={s.itemTitle}>{item.pesticide_name}</Text>

              <View style={s.metaRow}>
                <MaterialCommunityIcons name="account-circle" size={16} color={PADIPRO.muted} />
                <Text style={s.metaText}>{item.user_name || 'System'}</Text>
              </View>
              <View style={s.metaRow}>
                <Ionicons name="time-outline" size={16} color={PADIPRO.muted} />
                <Text style={s.metaText}>{formatDate(item.created_at)}</Text>
              </View>

              {item.note ? (
                <View style={[s.noteBox]}>
                  <Text style={s.noteText}>{item.note}</Text>
                </View>
              ) : null}
            </View>
          )}
          contentContainerStyle={{ paddingBottom: 12 }}
        />
      )}
    </View>
  );
}

const FilterChip = ({ label, active, onPress }) => (
  <TouchableOpacity
    onPress={onPress}
    style={[
      s.chip,
      active && { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary }
    ]}
  >
    <Text style={[s.chipText, active && { color: PADIPRO.primary, fontWeight: '700' }]}>{label}</Text>
  </TouchableOpacity>
);

const s = StyleSheet.create({
  screen: { flex: 1, padding: 12, backgroundColor: PADIPRO.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  headerActionBtn: {
    padding: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.32)',
  },

  summaryCard: {
    flexDirection: 'row', alignItems: 'stretch', padding: 10,
    backgroundColor: PADIPRO.surface, borderRadius: 12, borderWidth: 1, borderColor: PADIPRO.border,
    marginTop: 10, marginBottom: 10,
    ...padiproShadow,
  },
  summaryLabel: { color: PADIPRO.muted, fontSize: 12 },
  summaryValue: { fontSize: 18, fontWeight: '800', marginTop: 2, color: PADIPRO.text },
  summarySub: { color: PADIPRO.muted, fontSize: 12, marginTop: 2 },
  divider: { width: 1, backgroundColor: PADIPRO.border, marginHorizontal: 8 },

  searchWrap: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: PADIPRO.border, borderRadius: 10, backgroundColor: PADIPRO.surface,
    paddingRight: 4, marginBottom: 8
  },
  filterRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  chip: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 14, borderWidth: 1, borderColor: PADIPRO.border, backgroundColor: PADIPRO.surface, marginRight: 6 },
  chipText: { color: PADIPRO.text, fontSize: 12 },

  sectionTitle: { fontWeight: '700', color: PADIPRO.muted, marginTop: 10, marginBottom: 6 },

  card: {
    backgroundColor: PADIPRO.surface, borderRadius: 12, borderWidth: 1, borderColor: PADIPRO.border,
    padding: 10, marginBottom: 8,
    ...padiproShadow,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  badge: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, paddingVertical: 4, paddingHorizontal: 8, borderRadius: 20
  },
  qtyText: { fontWeight: '800', color: PADIPRO.text },

  itemTitle: { fontWeight: '700', fontSize: 15, marginTop: 8, marginBottom: 4, color: PADIPRO.text },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  metaText: { color: PADIPRO.muted },

  noteBox: { backgroundColor: PADIPRO.surfaceSunken, borderRadius: 8, padding: 8, marginTop: 8, borderWidth: 1, borderColor: PADIPRO.border },
  noteText: { color: PADIPRO.muted },
});
